import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { RevertIcon } from "@icons";
import { ReportSettingSpotExplanation } from "@common/helpers/report-settings/explain-report-setting-spot";
import { ReportSettingKey } from "@common/helpers/report-settings/report-setting-fields";
import styles from "./ReportSettings.module.scss";
import {
    describeResetTitle,
    describeResetValue,
    resetValueToText,
    TemplateNames,
} from "./reportSettingsSourceCopy";

interface Props<K extends ReportSettingKey> {
    field: K;
    explanation: ReportSettingSpotExplanation<K>;
    templateNames: TemplateNames;
    canEdit: boolean;
    onReset: () => void;
}

export const ResetToInheritedButton = <K extends ReportSettingKey>({
    field,
    explanation,
    templateNames,
    canEdit,
    onReset,
}: Readonly<Props<K>>) => {
    if (!canEdit || explanation.resetTo === null) {
        return null;
    }
    const title = describeResetTitle({
        source: explanation.resetTo.source,
        templateNames,
    });
    const valueParts = describeResetValue({
        field,
        value: explanation.resetTo.value,
    });
    const label = `${title}: ${resetValueToText(valueParts)}`;
    return (
        <Tooltip
            content={
                <VStack as="span" gap={4} className={styles.resetTooltip}>
                    <span>{title}:</span>
                    <span>
                        {valueParts.map((part, index) =>
                            part.kind === "variable" ? (
                                <span
                                    key={`${index}-${part.label}`}
                                    className={styles.resetTooltipVariable}
                                >
                                    {part.label}
                                </span>
                            ) : (
                                <span key={`${index}-${part.text}`}>
                                    {part.text}
                                </span>
                            ),
                        )}
                    </span>
                </VStack>
            }
        >
            <Button
                variant="secondary"
                size="sm"
                iconOnly
                aria-label={label}
                onClick={onReset}
            >
                <RevertIcon />
            </Button>
        </Tooltip>
    );
};
